import { AppInteraction } from '@org-avp/naap-app-sdk';
import { decodePairingToken } from '@org-avp/naap-models';
import { decodeJwt } from 'jose';
import * as pkg from '../../package.json';
import { initializeTracing, type TraceContextCarrier, withLinkedRootSpan } from './tracing';

type TraceObservationSource =
  | 'init'
  | 'context-token flow'
  | 'config event'
  | 'refresh action'
  | 'app start'
  | 'unknown';

interface TraceObservation {
  readonly traceContext: TraceContextCarrier | null;
  readonly source: TraceObservationSource;
  readonly timestamp: Date;
}

const TRACE_SOURCE_LABELS: Record<TraceObservationSource, string> = {
  init: 'Observed during init',
  'context-token flow': 'Observed while requesting context token',
  'config event': 'Observed from config event',
  'refresh action': 'Observed during manual refresh',
  'app start': 'Observed at app start',
  unknown: 'Observed from unknown path',
};

const traceTimeline: TraceObservation[] = [];

let lastTraceObservation: TraceObservation | null = null;
let previousTraceObservation: TraceObservation | null = null;

const THEME_STORAGE_KEY = 'naap-demo-iframe-theme';

type Theme = 'dark' | 'light';

function getElement(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/**
 * Reads the persisted theme preference, defaulting to dark.
 */
function getStoredTheme(): Theme {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Applies a theme to the document and syncs the toggle control's accessible state.
 */
function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);

  const toggle = getElement('themeToggle');
  if (!toggle) return;

  const isDark = theme === 'dark';
  toggle.setAttribute('aria-pressed', String(isDark));
  toggle.setAttribute('aria-label', isDark ? 'Switch to light theme' : 'Switch to dark theme');

  const label = toggle.querySelector('.theme-toggle__label');
  if (label) {
    label.textContent = isDark ? 'Dark' : 'Light';
  }
}

/**
 * Wires the light/dark theme toggle and persists the user's choice.
 */
function setupThemeToggle(): void {
  applyTheme(getStoredTheme());

  const toggle = getElement('themeToggle');
  if (!toggle) return;

  toggle.addEventListener('click', () => {
    const next: Theme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* localStorage may be unavailable; still apply for this session */
    }
    applyTheme(next);
  });
}

function getMirrors(id: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(`[data-mirror="${id}"]`));
}

function applyStateClass(element: HTMLElement, className: string): void {
  const baseClass = element.dataset.baseClass;
  const variantClasses = className
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => token !== 'status-badge' && token !== 'trace-pill');

  element.className = baseClass ? [baseClass, ...variantClasses].join(' ') : className;
}

function syncMirrors(id: string, mode: 'text' | 'html', className?: string): void {
  const source = getElement(id);
  if (!source) return;

  for (const mirror of getMirrors(id)) {
    if (mode === 'html') {
      mirror.innerHTML = source.innerHTML;
    } else {
      mirror.textContent = source.textContent ?? '';
    }

    if (source.dataset.error === 'true') {
      mirror.dataset.error = 'true';
    } else {
      delete mirror.dataset.error;
    }

    if (className) {
      applyStateClass(mirror, className);
    }
  }
}

/**
 * Updates element text and optionally its class
 */
function updateElement(id: string, text: string, className?: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.textContent = text;
  delete el.dataset.error;
  if (className) {
    applyStateClass(el, className);
  }
  syncMirrors(id, 'text', className);
}

/**
 * Displays an error in an element
 */
function setError(id: string, error: unknown): void {
  const el = document.getElementById(id);
  if (!el) return;
  const message = error instanceof Error ? error.message : String(error);
  el.textContent = `Error: ${message}`;
  el.dataset.error = 'true';
  syncMirrors(id, 'text');
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function setHtml(id: string, html: string): void {
  const el = getElement(id);
  if (!el) return;
  el.innerHTML = html;
  delete el.dataset.error;
  syncMirrors(id, 'html');
}

function formatOptionalTraceContextValue(value: string | undefined, fallback: string): string {
  return value?.trim() ? value : fallback;
}

function getBaggageFallback(observation: TraceObservation): string {
  return observation.traceContext?.baggage?.trim() ? observation.traceContext.baggage : 'Not present';
}

function hasTraceContextChanged(observation: TraceObservation): boolean {
  return (
    previousTraceObservation !== null &&
    previousTraceObservation.traceContext?.traceparent?.trim() !== observation.traceContext?.traceparent?.trim()
  );
}

function recordTraceObservation(
  traceContext: TraceContextCarrier | undefined,
  source: TraceObservationSource,
): TraceObservation {
  const normalizedTraceContext = traceContext
    ? {
        traceparent: traceContext.traceparent?.trim() || undefined,
        tracestate: traceContext.tracestate?.trim() || undefined,
        baggage: traceContext.baggage?.trim() || undefined,
      }
    : null;
  const observation: TraceObservation = {
    traceContext: normalizedTraceContext,
    source,
    timestamp: new Date(),
  };

  previousTraceObservation = lastTraceObservation;
  lastTraceObservation = observation;
  traceTimeline.push(observation);
  if (traceTimeline.length > 8) {
    traceTimeline.shift();
  }

  return observation;
}

function renderTraceBadges(observation: TraceObservation): void {
  const sourceBadge = getElement('traceSourceBadge');
  if (sourceBadge) {
    sourceBadge.textContent = TRACE_SOURCE_LABELS[observation.source];
    sourceBadge.className = 'trace-pill';
  }

  const changeBadge = getElement('traceChangeBadge');
  if (changeBadge) {
    const changed = hasTraceContextChanged(observation);
    changeBadge.textContent = changed ? 'Changed' : 'Stable';
    changeBadge.className = changed ? 'trace-pill trace-pill--warning' : 'trace-pill';
  }
}

function renderTraceTimeline(): void {
  const entries = [...traceTimeline].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  if (entries.length === 0) {
    setHtml(
      'traceTimeline',
      '<div class="sheet__trow"><time>—</time><span class="sheet__marker sheet__marker--pending"></span><div class="sheet__tbody"><b>No trace data yet</b><span class="sheet__tcode">The iframe has not observed a trace context. Start a session or refresh the app to capture the first carrier.</span></div></div>',
    );
    return;
  }

  setHtml(
    'traceTimeline',
    entries
      .map((entry, index) => {
        const changed = index === 0 && hasTraceContextChanged(entry);
        const markerClass = changed ? 'sheet__marker--warning' : 'sheet__marker--muted';
        const changeTag = changed ? ' <span class="sheet__tchange">· changed</span>' : '';
        const traceparent = entry.traceContext?.traceparent
          ? escapeText(entry.traceContext.traceparent)
          : 'No traceparent present';
        return `
          <div class="sheet__trow">
            <time>${escapeText(entry.timestamp.toLocaleTimeString())}</time>
            <span class="sheet__marker ${markerClass}"></span>
            <div class="sheet__tbody">
              <b>${escapeText(TRACE_SOURCE_LABELS[entry.source])}</b>${changeTag}
              <span class="sheet__tcode">${traceparent}</span>
            </div>
          </div>
        `;
      })
      .join(''),
  );
}

function renderTraceDetails(observation: TraceObservation): void {
  updateElement('traceparentValue', observation.traceContext?.traceparent ?? 'No traceparent observed yet');
  updateElement(
    'traceStateValue',
    formatOptionalTraceContextValue(observation.traceContext?.tracestate, 'Not present'),
  );
  updateElement('baggageValue', getBaggageFallback(observation));

  renderTraceBadges(observation);
  renderTraceTimeline();
}

function renderInitialTraceState(app: AppInteraction): void {
  const observation = recordTraceObservation(app.traceContext, 'init');
  renderTraceDetails(observation);
}

/**
 * Verifies context token with backend
 */
async function verifyToken(token: string, stage: string): Promise<string> {
  const stageUrl = stage === 'prod' ? '' : `.${stage}`;
  const response = await fetch(`https://api.naap-demo-iframe-app${stageUrl}.avp.tech/verify`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.ok ? 'verified successfully' : (await response.text()) || 'unknown verification error';
}

/**
 * Displays token and its decoded payload
 */
function displayToken(token: string): void {
  const decoded = decodePairingToken(token);
  updateElement('contextToken', token);
  updateElement('contextTokenPayload', JSON.stringify(decoded, null, 2));
}

/**
 * Displays token verification status
 */
async function displayVerificationStatus(token: string): Promise<void> {
  updateElement('verificationStatus', 'Verifying...', 'status-badge status-pending');

  const { stage } = decodePairingToken(token);
  const status = await verifyToken(token, stage ?? 'develop');

  const className = status.includes('successfully') ? 'status-badge status-success' : 'status-badge status-error';
  updateElement('verificationStatus', status, className);
}

/**
 * Updates last token refresh time display
 */
function setRefreshTime(token: string): void {
  const now = new Date();
  const jwt = decodeJwt(token);
  const expiresAt = jwt.exp ? new Date(jwt.exp * 1000) : null;
  const expiryText = expiresAt ? ` | Expires: ${expiresAt.toLocaleTimeString()}` : '';
  updateElement('contextTokenLastRefresh', `Last refreshed: ${now.toLocaleTimeString()}${expiryText}`);
}

/**
 * Refreshes the context token and updates UI
 */
async function refreshToken(app: AppInteraction): Promise<void> {
  try {
    updateElement('contextTokenPayload', 'Loading...');
    updateElement('contextToken', 'Loading...');
    updateElement('contextTokenLastRefresh', 'Refreshing token...');
    updateElement('verificationStatus', 'Pending', 'status-badge status-pending');

    const response = await app.requestContextToken({ withTraceContext: true });

    await withLinkedRootSpan('refresh context token', response.traceContext, async (span) => {
      span.setAttribute('context.token.length', response.token.length);
      renderTraceDetails(recordTraceObservation(response.traceContext, 'refresh action'));
      displayToken(response.token);
      await displayVerificationStatus(response.token);
      setRefreshTime(response.token);
    });
  } catch (error) {
    console.error('Failed to refresh context token:', error);
    setError('contextTokenPayload', error);
    setError('contextToken', error);
    updateElement('contextTokenLastRefresh', 'Token refresh failed');
    updateElement('verificationStatus', 'Error', 'status-badge status-error');
  }
}

/**
 * Displays app configuration
 */
function displayConfig(config: string): void {
  console.debug('📦 [DemoApp] Received app config!', { config, timestamp: new Date().toISOString() });
  updateElement('appConfig', config);
}

document.addEventListener('DOMContentLoaded', async () => {
  setupThemeToggle();
  initializeTracing();
  updateElement('versionInfo', `Demo App Version: ${pkg.version}`);

  try {
    const app = await AppInteraction.init();
    renderInitialTraceState(app);

    app.on('config', (config, traceContext) => {
      renderTraceDetails(recordTraceObservation(traceContext, 'config event'));
      withLinkedRootSpan('apply kiosk app configuration', traceContext, (span) => {
        span.setAttribute('config.length', config.length);
        displayConfig(config);
      });
    });

    const [_token, initialConfig] = await Promise.all([
      (async () => {
        const response = await app.requestContextToken({ withTraceContext: true });

        return withLinkedRootSpan('process context token', response.traceContext, async (span) => {
          span.setAttribute('context.token.length', response.token.length);
          renderTraceDetails(recordTraceObservation(response.traceContext, 'context-token flow'));
          displayToken(response.token);
          return displayVerificationStatus(response.token).then(() => {
            setRefreshTime(response.token);
            return response;
          });
        });
      })(),
      app.requestConfig({ withTraceContext: true }),
    ]);

    await withLinkedRootSpan('apply initial kiosk app configuration', initialConfig.traceContext, (span) => {
      span.setAttribute('config.length', initialConfig.config.length);
      renderTraceDetails(recordTraceObservation(initialConfig.traceContext, 'app start'));
      displayConfig(initialConfig.config);
    });

    // Setup event handlers
    for (const reloadButton of document.querySelectorAll<HTMLElement>('[data-action="reload"]')) {
      reloadButton.onclick = () => window.location.reload();
    }

    for (const refreshButton of document.querySelectorAll<HTMLElement>('[data-action="refresh-token"]')) {
      refreshButton.onclick = () => refreshToken(app);
    }

    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      console.info('Closing demo-iframe-app. Triggered by event:', event);
    };
  } catch (error) {
    setError('contextTokenPayload', error);
    setError('contextToken', error);
    setError('appConfig', error);
  }
});
